import { useQuery } from '@tanstack/react-query';
import { SwitchCase, When } from '@teamsparta/react';
import { Divider } from '@teamsparta/stack-divider';
import { Flex } from '@teamsparta/stack-flex';
import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';
import { isNotNil } from '@teamsparta/utils';

import type { GetCommunityPostCommentsResponseDto } from '@/api/community/comment/comment.dto';
import type { CommunityPost } from '@/api/community/post';
import { Dot } from '@/components/common/Dot';
import { EditorContent } from '@/components/common/Editor';
import Only from '@/components/common/Only';
import { Skeleton } from '@/components/common/skeleton';
import { useToken } from '@/hooks';
import { profileQuery } from '@/hooks/react-query/community/profile';
import { adjustDateByPurpose, getTimePastText } from '@/lib/utils/date';
import { usePoll } from '@/pages/community/logic';
import {
  Actions,
  BluredPoll,
  DidNotPoll,
  DidPoll,
  PollMetadata,
  PollSkeleton,
  ProfileImage,
} from '@/pages/community/shared';

import { PostDetailCategory } from '../PostDetailCategory';
import {
  Comments,
  CreateCommentForm,
  LikeButton,
  ShareButton,
  SortBy,
} from './components';
import {
  useDeletePost,
  useEditPost,
  useLike,
  useShare,
  useSortBy,
} from './logic';
import * as S from './style';

interface Props {
  postDetail: CommunityPost;
  comments: GetCommunityPostCommentsResponseDto | null;
}

export function PostDetail({
  postDetail: {
    _id,
    author,
    category,
    content,
    createdAt,
    title,
    likesCount,
    poll,
    commentsCount,
    isMyLike,
  },
  comments: commentsFromServer,
}: Props) {
  // profile
  const { data: profile } = useQuery(profileQuery);
  const isAuthor = profile?._id === author._id;
  const isAdmin = profile?.role === 'admin';

  // poll
  const { pollComponentType } = usePoll(poll);
  const hasPoll = isNotNil(poll);
  const totalSelectedCount =
    poll?.pollOptions.reduce((acc, curr) => acc + curr.selectedCount, 0) ?? 0;

  // interaction
  const { handleEdit } = useEditPost();
  const { handleDelete, isPending: isDeletePending } = useDeletePost();
  const { handleLike } = useLike(_id);
  const { handleShare } = useShare(_id);
  const { sortBy, handleChangeSortBy } = useSortBy(_id);

  const { token } = useToken();

  return (
    <S.PostDetailContainer key={_id}>
      <Flex.Column gap={{ mobile: 24, desktop: 30 }} fullWidth>
        <Flex.Column gap={8} fullWidth>
          <Flex.Row fullWidth align='center' justify='between'>
            <PostDetailCategory>{category}</PostDetailCategory>
            <When condition={Boolean(token) && (isAuthor || isAdmin)}>
              <Actions
                deleteTitle='글을 정말 삭제하시겠습니까?'
                deleteDescription='한번 삭제한 글은 복구할 수 없습니다'
                isDeletePending={isDeletePending}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            </When>
          </Flex.Row>
          <Text
            as='h2'
            font='title2'
            color={vars.neutral[5]}
            style={{ wordBreak: 'break-word' }}
          >
            {title}
          </Text>
          <Flex.Row align='center' gap={6} fullWidth>
            <ProfileImage
              alt='항해 갈매기'
              width={20}
              height={20}
              src={author.imageUrl}
            />
            <Text as='span' font='tag1Sb' color={vars.neutral[50]}>
              {author.nickname}
            </Text>
            <Dot />
            <Text as='span' font='tag1M' color={vars.neutral[60]}>
              {getTimePastText(adjustDateByPurpose(createdAt, 'calc'))}
            </Text>
          </Flex.Row>
        </Flex.Column>
        <Divider color={vars.neutral[80]} />
        <EditorContent content={content} />
        {hasPoll && (
          <When
            condition={pollComponentType !== 'Loading'}
            fallback={
              <Flex.Column gap={16} fullWidth>
                <PollSkeleton />
                <Skeleton style={{ width: '120px', height: '20px' }} />
              </Flex.Column>
            }
          >
            <SwitchCase
              value={pollComponentType}
              cases={{
                BluredPoll: <BluredPoll postId={_id} />,
                DidNotPoll: (
                  <Flex.Column fullWidth gap={16}>
                    <DidNotPoll postId={_id} pollOptions={poll?.pollOptions} />
                    <PollMetadata
                      pollEndDate={poll.pollEndDate}
                      totalSelectedCount={totalSelectedCount}
                    />
                  </Flex.Column>
                ),
                DidPoll: (
                  <Flex.Column fullWidth gap={16}>
                    <DidPoll pollOptions={poll?.pollOptions} />
                    <PollMetadata
                      pollEndDate={poll.pollEndDate}
                      totalSelectedCount={totalSelectedCount}
                    />
                  </Flex.Column>
                ),
              }}
            />
          </When>
        )}
        <Flex.Row align='center' justify='center' gap={8} fullWidth>
          <LikeButton
            count={likesCount}
            liked={isMyLike && Boolean(token)}
            onClick={handleLike}
          />
          <ShareButton onClick={handleShare} />
        </Flex.Row>
      </Flex.Column>
      <Only.Mobile>
        <S.ExtendedDivider>
          <Divider color={vars.neutral[80]} size={8} />
        </S.ExtendedDivider>
      </Only.Mobile>
      <Only.Desktop>
        <Divider color={vars.neutral[80]} />
      </Only.Desktop>
      <Flex.Column gap={30} fullWidth>
        <Flex.Column fullWidth gap={20}>
          <Flex.Row fullWidth justify='between'>
            <Flex.Row align='center' gap={4}>
              <Text as='span' font='captionSb' color={vars.neutral[5]}>
                댓글
              </Text>
              <Text as='span' font='captionSb' color={vars.brand.default}>
                {commentsCount}
              </Text>
            </Flex.Row>

            <SortBy value={sortBy} onChange={handleChangeSortBy} />
          </Flex.Row>
          <CreateCommentForm postId={_id} />
        </Flex.Column>
        <Comments postId={_id} comments={commentsFromServer} sortBy={sortBy} />
      </Flex.Column>
    </S.PostDetailContainer>
  );
}
